import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Box, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { GridColDef } from '@mui/x-data-grid-pro';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterLuxon } from '@mui/x-date-pickers-pro/AdapterLuxon';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SortDTO } from '../../api';
import { HeapRecordingFilterDTO, UpdateHeapRecordingDTO, UserPopulatedDTO } from '../../api/generated';
import { useUpdateHeapRecordingBatch } from '../../api/mutations/heap/useUpdateHeapRecordingBatch';
import { useHeapRecordingFilterOptions } from '../../api/queries/heapRecordingFilterOptions';
import { HeapRecordingBatch, useHeapRecordingList } from '../../api/queries/heapRecordingList';
import { useHeapMaskPreferences } from '../Heap/useHeapMaskPreferences';
import { RESPONSIVE_DRAWER_HEIGHT } from '../../Constants';
import i18n from '../../i18n';
import { localizedDateFormatter, skipUndefined, useSyncState } from '../../utils';
import { DetailContainerLayout } from '../DetailView/DetailContainerLayout';
import { DetailTitlebar } from '../DetailView/DetailTitlebar';
import { ListIndexNavigation } from '../DetailView/ListIndexNavigation';
import { ListWalkNavigation } from '../DetailView/ListWalkNavigation';
import { ActiveFilterChips } from '../Filter/ActiveFilterChips';
import { CombinedFilterList } from '../Filter/CombinedFilterList';
import { FilterSpec } from '../Filter/common';
import { FilterHeaderLayout } from '../Filter/FilterHeaderLayout';
import { FilterPanelHeader } from '../Filter/FilterPanelHeader';
import { FilterPanelBodyLayout, FilterPanelContainer } from '../Filter/FilterPanelLayout';
import { ResetFiltersButton } from '../Filter/ResetFiltersButton';
import { MenuBar } from '../MenuBar';
import { useSnackbar } from '../SnackbarProvider';
import { HeapRecordingsDetails } from './HeapRecordingsDetails';
import { RecordingsList, RecordingSorting } from './RecordingsList';

export interface HeapRecordingsProps {
  user: UserPopulatedDTO;
}

const defaultFilter: HeapRecordingFilterDTO = { archived: { eq: false } };

export function HeapRecordings({ user }: HeapRecordingsProps): ReactElement {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const updateRecordingBatchMutation = useUpdateHeapRecordingBatch();
  const { preferences: maskPreferences, setMaskVisibility, setMaskOpacity } = useHeapMaskPreferences();

  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false);
  const [filter, setFilter] = useState<HeapRecordingFilterDTO>(defaultFilter);
  const [transientFilter, transientFilterSync, setTransientFilter] =
    useSyncState<HeapRecordingFilterDTO>(defaultFilter);
  const [recordingsPage, setRecordingsPage] = useState(0);
  const [recordingsSorting, setRecordingsSorting] = useState<SortDTO<HeapRecordingBatch>>(defaultSorting);

  const { data: filterOptions } = useHeapRecordingFilterOptions({
    jobsiteId: user.preferredJobsite?.id,
    filter,
  });
  const { data: batches } = useHeapRecordingList({
    jobsiteId: user.preferredJobsite?.id,
    filter,
    pageSize,
    pageNumber: recordingsPage,
    sorting: recordingsSorting,
  });

  const extraFieldColumns = useMemo((): GridColDef[] => {
    if (!batches?.data) return [];
    const keys = new Set<string>();
    for (const batch of batches.data) {
      const extraFields = batch.cameras[0]?.extraFields;
      if (extraFields) {
        for (const key of Object.keys(extraFields)) {
          keys.add(key);
        }
      }
    }
    return Array.from(keys).map((key) => ({
      headerName: `heap.extraFields.labels.${key}`,
      field: `extraField_${key}`,
      resizable: false,
      sortable: false,
      flex: 0.4,
      // extra fields are per-camera, column value hold the value of the first camera
      valueGetter: (_: unknown, row: HeapRecordingBatch) => row.cameras[0]?.extraFields?.[key],
      renderCell: (params) =>
        params.value !== undefined
          ? t(`heap.extraFields.values.${key}`, {
              value: params.value,
              defaultValue: String(params.value),
            })
          : null,
    }));
  }, [batches?.data, t]);

  const [selectedBatchIdx, setSelectedBatchIdx] = useState<number | undefined>(undefined);
  const selectedBatch: HeapRecordingBatch | undefined =
    batches && selectedBatchIdx !== undefined && Math.floor(selectedBatchIdx / pageSize) === recordingsPage
      ? batches.data[selectedBatchIdx % pageSize]
      : undefined;

  const handleRowSelect = useCallback(
    function (recordingId: string | null): void {
      if (batches) {
        const idx = batches.data.findIndex((v) => v.id === recordingId);
        if (idx >= 0) {
          setSelectedBatchIdx(idx + recordingsPage * pageSize);

          return;
        }
      }
      setSelectedBatchIdx(undefined);
    },
    [batches, recordingsPage]
  );

  useEffect(
    function movePageToCoverSelection() {
      if (batches && selectedBatchIdx !== undefined) {
        const selectionPage = Math.floor(selectedBatchIdx / pageSize);
        if (selectionPage !== recordingsPage) {
          setRecordingsPage(selectionPage);
          setSelectedBatchIdx(selectionPage * pageSize + (selectedBatchIdx % pageSize));
        }
      }
    },
    [selectedBatchIdx, recordingsPage, batches]
  );

  const handleCloseDetails = useCallback(function (): void {
    setSelectedBatchIdx(undefined);
  }, []);

  const handleSortChange = useCallback(
    function (sort: RecordingSorting | null): void {
      handleCloseDetails();
      setRecordingsSorting(sort ? { sortField: sort.field, sortDirection: sort.sort } : defaultSorting);
    },
    [handleCloseDetails]
  );

  const handleFilterCommit = useCallback(
    function (): void {
      setFilter(transientFilterSync.current);
    },
    [transientFilterSync]
  );

  useEffect(
    function syncTransientFilter(): void {
      setTransientFilter(filter);
    },
    [filter, setTransientFilter]
  );

  const handleRecordingsPageChangeByUser = useCallback(
    function (newPage: number) {
      handleCloseDetails();
      setRecordingsPage(newPage);
    },
    [handleCloseDetails]
  );

  async function handleHeapRecordingUpdate(batchId: string, update: Partial<UpdateHeapRecordingDTO>): Promise<void> {
    const jobsiteId = user.preferredJobsite?.id;
    if (!jobsiteId) return;

    try {
      await updateRecordingBatchMutation.mutateAsync({ jobsiteId, batchId, update });
      showSnackbar(update.archived ? t('heap.archiveSuccess') : t('heap.unarchiveSuccess'), 'success');
    } catch {
      showSnackbar(update.archived ? t('heap.archiveError') : t('heap.unarchiveError'), 'error');
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
      <Box display='flex'>
        <MenuBar />
        <Grid
          container
          columnSpacing={2}
          sx={{
            height: `calc(100% - ${RESPONSIVE_DRAWER_HEIGHT}px)`,
            width: '100%',
            top: RESPONSIVE_DRAWER_HEIGHT,
            position: 'absolute',
            overflow: 'hidden',
          }}
          padding={2}
        >
          {/* Column 1: Filters */}
          <Grid
            size={{ md: isFilterCollapsed ? 0.25 : 3 }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              transition: 'all 0.3s ease-in-out',
            }}
          >
            {isFilterCollapsed ? (
              <Box
                onClick={() => setIsFilterCollapsed(false)}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover .chevron-icon': {
                    color: 'primary.main',
                  },
                  '&:hover .divider-line': {
                    borderColor: 'primary.main',
                  },
                }}
              >
                {/* Top divider line */}
                <Box
                  className='divider-line'
                  sx={{
                    flex: 1,
                    width: '2px',
                    backgroundColor: 'divider',
                    transition: 'background-color 0.2s ease-in-out',
                  }}
                />

                {/* Chevron in circle */}
                <Tooltip title={t('common.expandFilters')} placement='right'>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: 'background.paper',
                      border: '2px solid',
                      borderColor: 'divider',
                      transition: 'border-color 0.2s ease-in-out',
                      my: 1,
                    }}
                  >
                    <ChevronRightIcon
                      className='chevron-icon'
                      sx={{
                        color: 'text.secondary',
                        fontSize: '1.25rem',
                        transition: 'color 0.2s ease-in-out',
                      }}
                    />
                  </Box>
                </Tooltip>

                {/* Bottom divider line */}
                <Box
                  className='divider-line'
                  sx={{
                    flex: 1,
                    width: '2px',
                    backgroundColor: 'divider',
                    transition: 'background-color 0.2s ease-in-out',
                  }}
                />
              </Box>
            ) : (
              <Paper elevation={8} sx={{ borderRadius: '10px', height: '100%', position: 'relative' }}>
                <Tooltip title={t('common.collapseFilters')} placement='right'>
                  <IconButton
                    onClick={() => setIsFilterCollapsed(true)}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      zIndex: 10,
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                </Tooltip>
                <FilterPanelContainer>
                  <FilterPanelHeader />
                  <FilterPanelBodyLayout>
                    <CombinedFilterList
                      filterSpec={recordingsFilterSpec}
                      filter={transientFilter}
                      filterOptions={filterOptions || {}}
                      onFilterChange={setTransientFilter}
                      onCommit={handleFilterCommit}
                    />
                  </FilterPanelBodyLayout>
                </FilterPanelContainer>
              </Paper>
            )}
          </Grid>

          {/* Column 2: Recordings List */}
          <Grid
            size={{
              md: selectedBatchIdx === undefined ? (isFilterCollapsed ? 8.25 : 5.5) : isFilterCollapsed ? 5.25 : 3.5,
            }}
            sx={{ display: 'flex', flexDirection: 'column', height: '100%', transition: 'all 0.3s ease-in-out' }}
          >
            <Paper
              elevation={8}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                borderRadius: '10px',
                boxSizing: 'border-box',
                padding: 2,
              }}
            >
              <FilterHeaderLayout>
                <ResetFiltersButton filter={filter} onFilterChange={setFilter} />
                <ActiveFilterChips filterSpec={recordingsFilterSpec} filter={filter} onFilterChange={setFilter} />
              </FilterHeaderLayout>
              <RecordingsList
                columns={[...columns, ...extraFieldColumns]}
                data={batches || null}
                selectedId={selectedBatch?.id}
                onSelect={handleRowSelect}
                pageSize={pageSize}
                page={recordingsPage}
                onPageChange={handleRecordingsPageChangeByUser}
                onSortChange={handleSortChange}
                getRowClassName={(params) => (params.row.archived ? 'archived-row' : '')}
              />
            </Paper>
          </Grid>

          {/* Column 3: Details */}
          <Grid
            size={{ md: selectedBatchIdx === undefined ? 3.5 : isFilterCollapsed ? 6.5 : 5.5 }}
            sx={{ height: '100%', transition: 'all 0.3s ease-in-out' }}
          >
            <DetailContainerLayout elevation={8}>
              {selectedBatchIdx !== undefined && selectedBatch ? (
                <>
                  <DetailTitlebar onClose={handleCloseDetails} title={t('recording.details')}>
                    <Box>
                      <ListIndexNavigation
                        Component={ListWalkNavigation}
                        maxIdx={batches?.total}
                        currentIdx={selectedBatchIdx}
                        onIndexChange={setSelectedBatchIdx}
                      />
                    </Box>
                  </DetailTitlebar>
                  <HeapRecordingsDetails
                    key={selectedBatch.id}
                    batch={selectedBatch}
                    onHeapRecordingUpdate={handleHeapRecordingUpdate}
                    maskPreferences={maskPreferences}
                    onMaskVisibilityChange={setMaskVisibility}
                    onMaskOpacityChange={setMaskOpacity}
                  />
                </>
              ) : (
                <Box display='flex' alignItems='center' justifyContent='center' height='100%'>
                  <Typography variant='body1' textAlign='center'>
                    {t('recording.selectForDetails')}
                  </Typography>
                </Box>
              )}
            </DetailContainerLayout>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
}

const pageSize = 50;
const defaultSorting: SortDTO<HeapRecordingBatch> = {
  sortDirection: 'desc',
  sortField: 'recordedAt',
};

const recordingsFilterSpec: FilterSpec[] = [
  { filterKey: 'recorded_at', type: 'DateRange', filterProps: {}, displayName: 'heap.recordedAt' },
  { filterKey: 'license_plate', type: 'StringSelect', filterProps: {}, displayName: 'common.licensePlate' },
  {
    filterKey: 'has_detections',
    type: 'BinaryChoice',
    filterProps: {
      trueOptionIcon: <CheckCircleOutlineIcon color='success' />,
      falseOptionIcon: <HighlightOffIcon color='error' />,
    },
    displayName: 'heap.hasDetections',
  },
  { filterKey: 'version', type: 'StringSelect', filterProps: {}, displayName: 'heap.version' },
  {
    filterKey: 'archived',
    type: 'BinaryChoice',
    filterProps: {},
    displayName: 'heap.showArchived',
  },
];

const columns: GridColDef[] = [
  {
    headerName: 'heap.recordedAt',
    field: 'recordedAt',
    resizable: false,
    flex: 0.7,
    type: 'dateTime',
    valueFormatter: localizedDateFormatter,
  },
  {
    headerName: 'common.licensePlate',
    field: 'licensePlate',
    resizable: false,
    sortable: false,
    flex: 0.5,
    type: 'string',
    valueFormatter: (value) => skipUndefined(value, (v: string) => v.toUpperCase()),
  },
  {
    headerName: 'heap.hasDetections',
    field: 'hasDetections',
    resizable: false,
    flex: 0.3,
    type: 'boolean',
    renderCell: ({ value }) =>
      value ? <CheckCircleOutlineIcon color='success' /> : <HighlightOffIcon color='error' />,
  },
  {
    headerName: 'heap.version',
    field: 'version',
    resizable: false,
    sortable: false,
    flex: 0.3,
    type: 'string',
  },
];
